import * as Types from '../gql/_types.js';

import { BookingModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = BookingModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetBookingsQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.bookings.errors = [];
    state.bookings.loading = true;
    try {
        let { limit, offset, orderBy } = state.bookings.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetBookings({...originalPayload, ...finalPayload})
        state.bookings.meta.total = meta.aggregate?.count || 0;
        state.bookings.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
    }
    state.bookings.loading = false;
}

export const resetState: Action = ({state}) => {
    state.bookings.errors = []
    state.bookings.loading = false
    state.bookings.items = {}
    state.bookings.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.bookings.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.bookings.resetMeta()
    actions.bookings.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.bookings.errors = [];
    state.bookings.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.bookings.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetBookings({...originalPayload, ...finalPayload})
        state.bookings.meta.total = meta.aggregate?.count || 0;
        state.bookings.meta.page = newPage;
        state.bookings.meta.offset = newOffset;
        state.bookings.items = { ...state.bookings.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
    }
    state.bookings.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetBookingsQueryVariables> = async ({state, effects}, payload) => {
    state.bookings.errors = [];
    state.bookings.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetBookings(payload)
        state.bookings.meta.total = meta.aggregate?.count || 0;
        state.bookings.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
    }
    state.bookings.loading = false;
}

export const getItem: AsyncAction<Types.GetBookingQueryVariables,Types.GetBookingQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetBooking(payload);
        return item;
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetBookingQueryVariables> = async ({state, actions}, payload) => {
    state.bookings.loading = true;
    try {
        let item = await actions.bookings.getItem(payload);
        if (item) {
            state.bookings.currentItemId = item.id;
            state.bookings.items[item.id] = item;
        }
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
    }
    state.bookings.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.bookings.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.bookings.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.BookingFragment>> = async ({state}, meta) => {
    state.bookings.meta = {
        ...state.bookings.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateBookingMutationVariables['object'],Types.CreateBookingMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateBookingMutationVariables['object']>(payload);
    state.bookings.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateBooking({object:finalPayload});
        return item;
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateBookingMutationVariables,Types.UpdateBookingMutation['item']> = async ({state, effects}, payload) => {
    state.bookings.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateBooking(payload);
        return item;
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteBookingMutationVariables,Types.DeleteBookingMutation['item']> = async ({state, effects}, payload) => {
    state.bookings.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteBooking(payload);
        return item;
    } catch (e: any) {
        state.bookings.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetBookingsMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetBookingsMetaSubscription, Types.SyncGetBookingsMetaSubscriptionVariables>(
        'syncGetBookingsMeta', 
        subscriptions.SyncGetBookingsMeta, 
        { where }, 
        actions.bookings.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetBookingsMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetBookingsMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.bookings.errors = [ ...state.bookings.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.bookings.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetBookingsSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.bookings.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.bookings.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetBookingsSubscription, Types.SyncGetBookingsSubscriptionVariables>(
        'syncGetBookings', 
        subscriptions.SyncGetBookings, 
        {...originalPayload, ...payload}, 
        actions.bookings.onItemsData,
        () => { state.bookings.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetBookings');
    state.bookings.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetBookingsSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.bookings.errors = [ ...state.bookings.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.bookings.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetBookingsSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.bookings.syncGetItems(finalPayload);
    actions.bookings.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.bookings.unsubItemsData();
    actions.bookings.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetBookingSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.bookings.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetBookingSubscription, Types.SyncGetBookingSubscriptionVariables>(
        'syncGetBooking', 
        subscriptions.SyncGetBooking, 
        payload, 
        actions.bookings.onItemData,
        () => { state.bookings.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetBooking');
    state.bookings.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetBookingSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.bookings.errors = [ ...state.bookings.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.bookings.currentItemId = payload.data.item.id;
        state.bookings.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetBookingSubscriptionVariables> = async ({actions}, payload) => {
    actions.bookings.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.bookings.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};